/*
 * FICHIER: Sondage.js
 * BUT: Composant de sondage interactif pour les utilisateurs
 * 
 * Ce composant permet aux utilisateurs de participer à des sondages :
 * - Affichage d'une question avec options multiples
 * - Interface interactive pour sélectionner une réponse
 * - Soumission des votes avec validation
 * - Affichage des résultats en temps réel avec graphiques
 * - Prévention du vote multiple par utilisateur
 * - États de chargement et gestion d'erreurs
 * 
 * FONCTIONNALITÉS AVANCÉES :
 * - Barres de progression animées pour les résultats
 * - Mise en évidence du choix de l'utilisateur
 * - Persistence du vote (localStorage/cookies)
 * - Animations et effets visuels (fade-in, shine)
 * - Interface responsive et accessible
 * - Gestion des états (loading, voted, error)
 * 
 * PATTERN :
 * - Hooks pour la gestion d'état complexe
 * - Service séparé pour la logique métier
 * - Styled-components avec animations keyframes
 * - Conditional rendering selon l'état
 */

import React, { useState, useEffect, useRef } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import { FaPoll, FaVoteYea, FaChartBar, FaCheckCircle, FaCrown } from 'react-icons/fa';
import styled, { keyframes } from 'styled-components';
import { BsCheckCircleFill, BsCircle } from 'react-icons/bs';
import SimplePollService from '../../services/SimplePollService';
import { createClubUrl, createUrlFriendlyString } from '../../utils/slug';

// Animations keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shine = keyframes`
  from {
    background-position: 200% center;
  }
  to {
    background-position: -200% center;
  }
`;

// Composants stylés
const PollContainer = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
  border-radius: 10px;
  background-color: var(--white);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  animation: ${fadeIn} 0.5s ease-out;
`;

const PollHeader = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const PollTitle = styled.h2`
  font-size: var(--font-size-xl);
  color: var(--dark);
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    margin-right: 0.5rem;
    color: var(--primary);
  }
`;

const PollDescription = styled.p`
  font-size: var(--font-size-md);
  color: var(--gray);
  line-height: 1.6;
`;

const PollQuestion = styled.h3`
  font-size: var(--font-size-lg);
  color: var(--dark);
  margin-bottom: 1.5rem;
  text-align: center;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const OptionButton = styled.button`
  background: ${props => props.$selected ? 'var(--primary-light)' : 'var(--white)'};
  border: 2px solid ${props => props.$selected ? 'var(--primary)' : 'var(--gray-lighter)'};
  border-radius: var(--border-radius-lg);
  padding: 1rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all var(--transition-normal);
  display: flex;
  align-items: center;
  justify-content: space-between;
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:hover {
    border-color: ${props => props.disabled ? 'var(--gray-lighter)' : 'var(--primary)'};
    transform: ${props => props.disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.disabled ? 'none' : 'var(--shadow-sm)'};
  }
`;

const OptionText = styled.span`
  font-size: var(--font-size-md);
  color: var(--dark);
  flex: 1;
  text-align: left;
`;

const OptionIcon = styled.div`
  color: ${props => props.$selected ? 'var(--primary)' : 'var(--gray)'};
  font-size: 1.2rem;
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, var(--primary), var(--primary-dark));
  color: var(--white);
  border: none;
  border-radius: var(--border-radius-lg);
  padding: 1rem 2rem;
  font-size: var(--font-size-md);
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all var(--transition-normal);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:hover {
    transform: ${props => props.disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.disabled ? 'none' : 'var(--shadow-md)'};
  }

  &.shine {
    background: linear-gradient(
      135deg,
      var(--primary),
      var(--primary-light),
      var(--primary),
      var(--primary-dark)
    );
    background-size: 200% 100%;
    animation: ${shine} 2s ease-in-out infinite;
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: var(--primary);
  border: 2px solid var(--primary);
  border-radius: var(--border-radius-lg);
  padding: 0.75rem 1.5rem;
  font-size: var(--font-size-md);
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all var(--transition-normal);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  opacity: ${props => props.disabled ? 0.6 : 1};
  margin-top: 0.75rem;

  &:hover {
    background: var(--primary-light);
    transform: ${props => props.disabled ? 'none' : 'translateY(-2px)'};
  }
`;

const UpcomingDateMessage = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  padding: 1rem;
  background: var(--primary-light);
  border-radius: var(--border-radius-lg);
  color: white;
  font-weight: 600;
  font-size: var(--font-size-md);
`;

const PreviousWinnerBlock = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: var(--light);
  border-radius: var(--border-radius-lg);
  border: 1px solid var(--gray-lighter);
  font-size: var(--font-size-sm);
  color: var(--gray);
  
  strong {
    color: var(--dark);
  }
  
  svg {
    color: #d4af37;
    margin-right: 0.35rem;
    vertical-align: middle;
  }
`;

const ResultsContainer = styled.div`
  margin-top: 2rem;
`;

const ResultsTitle = styled.h3`
  font-size: var(--font-size-lg);
  color: var(--dark);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    margin-right: 0.5rem;
    color: var(--primary);
  }
`;

const ResultItem = styled.div`
  margin-bottom: 1rem;
  padding: 1rem;
  border-radius: var(--border-radius-md);
  background: ${props => props.$isUserChoice ? 'var(--primary-light)' : 'var(--light)'};
  border: 2px solid ${props => props.$isUserChoice ? 'var(--primary)' : 'transparent'};
`;

const ResultHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const ResultText = styled.span`
  font-weight: 600;
  color: var(--dark);
  display: flex;
  align-items: center;

  svg {
    margin-left: 0.5rem;
    color: var(--primary);
  }
`;

const ResultVotes = styled.span`
  font-size: var(--font-size-sm);
  color: var(--gray);
`;

const ResultBar = styled.div`
  background: var(--gray-lighter);
  border-radius: var(--border-radius-sm);
  height: 8px;
  overflow: hidden;
`;

const ResultProgress = styled.div`
  background: ${props => props.$isUserChoice ? 'var(--primary)' : 'var(--primary-light)'};
  height: 100%;
  width: ${props => props.$percentage}%;
  transition: width 1s ease-out;
  border-radius: var(--border-radius-sm);
`;

const TotalVotes = styled.div`
  text-align: center;
  margin-top: 1rem;
  font-size: var(--font-size-sm);
  color: var(--gray);
`;

const ErrorMessage = styled.div`
  background: var(--error-light);
  color: var(--error);
  border: 1px solid var(--error);
  border-radius: var(--border-radius-md);
  padding: 1rem;
  margin-bottom: 1rem;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid var(--gray-lighter);
  border-top: 3px solid var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Ajout d'un composant utilitaire pour le logo club
const ClubLogoRound = ({ logo, alt, onClick, hovered }) => (
  <span
    style={{
      display: 'inline-block',
      width: 32,
      height: 32,
      borderRadius: '50%',
      background: logo ? `url(${logo}) center/cover no-repeat` : '#e0e0e0',
      marginRight: 10,
      verticalAlign: 'middle',
      boxShadow: hovered ? '0 0 8px #4a6bce55' : 'none',
      transform: hovered ? 'scale(1.08)' : 'scale(1)',
      transition: 'all 0.2s',
      cursor: 'pointer',
      border: '2px solid #f5f5f5',
    }}
    title={alt}
    onClick={onClick}
  />
);

// Composant principal
const SimplePoll = () => {
  const [poll, setPoll] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [voteSubmitted, setVoteSubmitted] = useState(false);
  const [results, setResults] = useState(null);
  const [userChoice, setUserChoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [timeUntilNextVote, setTimeUntilNextVote] = useState(0);
  const [hoveredOption, setHoveredOption] = useState(null);
  const [hoveredResult, setHoveredResult] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  const [pollFinished, setPollFinished] = useState(false);
  const [originalOptionsOrder, setOriginalOptionsOrder] = useState(null); // Stocker l'ordre original pour le vote
  const [showResultsPreview, setShowResultsPreview] = useState(false);   // Aperçu des résultats avant de voter
  const [resultsPreviewLoading, setResultsPreviewLoading] = useState(false);
  const [isUpcoming, setIsUpcoming] = useState(false);                   // Sondage actif mais votes pas encore ouverts (Date_Debut > aujourd'hui)
  const [votesStartDate, setVotesStartDate] = useState(null);             // Date d'ouverture des votes (pour affichage)
  const [previousWinner, setPreviousWinner] = useState(null);             // Gagnant du dernier sondage terminé { pollTitle, winnerText, winnerVotes, totalVotes }

  // Fonction pour mélanger aléatoirement un tableau (algorithme Fisher-Yates)
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Formate le temps restant (en heures décimales) en "Xh YYmin" / "X h" / "X min"
  const formatTimeUntilNextVote = (hours) => {
    const totalMinutes = Math.max(0, Math.ceil(hours * 60));
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    if (h > 0 && m > 0) return `${h} h ${m} min`;
    if (h > 0) return `${h} h`;
    return `${m} min`;
  };

  // Charger le sondage actif et vérifier l'état du vote
  useEffect(() => {
    const loadPoll = async () => {
      try {
        setLoading(true);
        const response = await SimplePollService.getCurrentPoll();
        
        if (response.success && response.poll) {
          let pollData = response.poll;
          const today = new Date();
          const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          const endDate = response.poll.endDate ? new Date(response.poll.endDate) : null;
          const startDate = response.poll.startDate ? new Date(response.poll.startDate) : null;
          const endDateAtMidnight = endDate ? new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()) : null;
          const startDateAtMidnight = startDate ? new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()) : null;
          const statutLower = (response.poll.statut || '').toLowerCase();

          // Sondage terminé (Statut Terminé ou Date_Fin < aujourd'hui) → afficher les résultats uniquement
          const isFinishedByDate = endDateAtMidnight ? endDateAtMidnight < startOfToday : false;
          const isFinishedByStatus = statutLower === 'terminé' || statutLower === 'termine';
          if (isFinishedByDate || isFinishedByStatus) {
            setHasVoted(true);
            setVoteSubmitted(true);
            setPollFinished(true);
            const pollResults = await SimplePollService.getPollResults(response.poll.documentId || response.poll.id);
            setResults(pollResults);
            setPoll(pollData);
            return;
          }

          // Sondage actif mais date de début des votes pas encore atteinte → afficher les clubs à venir + date d'ouverture
          const isActif = statutLower === 'actif';
          const isUpcomingPoll = isActif && startDateAtMidnight && startDateAtMidnight > startOfToday;
          if (isUpcomingPoll) {
            setIsUpcoming(true);
            setVotesStartDate(response.poll.startDate);
            setPoll(pollData);
            const winnerData = await SimplePollService.getLastFinishedPollWinner();
            setPreviousWinner(winnerData);
            return;
          }

          // Vérifier si l'utilisateur a déjà voté (méthode async)
          // Utiliser documentId (stable) au lieu de id (peut changer lors des modifications)
          const pollDocumentId = response.poll.documentId || response.poll.id;
          const hasUserVoted = await SimplePollService.hasVotedInLast24Hours(pollDocumentId);
          setHasVoted(hasUserVoted);
          
          // Stocker l'ordre original des options pour la soumission du vote
          if (pollData.question && pollData.question.options && pollData.question.options.length > 0) {
            setOriginalOptionsOrder([...pollData.question.options]);
          }
          
          if (hasUserVoted) {
            // Charger les résultats et le choix de l'utilisateur (méthode async)
            const userChoice = await SimplePollService.getUserChoice(pollDocumentId);
            setUserChoice(userChoice);
            setVoteSubmitted(true); // Afficher directement les résultats
            const pollResults = await SimplePollService.getPollResults(response.poll.documentId || response.poll.id);
            setResults(pollResults);
            // Calculer le temps restant
            const timeLeft = SimplePollService.getTimeUntilNextVote(pollDocumentId);
            setTimeUntilNextVote(timeLeft);
          } else {
            // Si l'utilisateur peut voter, mélanger aléatoirement l'ordre des clubs
            // pour éviter l'utilisation de robots autoclic
            if (pollData.question && pollData.question.options && pollData.question.options.length > 0) {
              pollData = {
                ...pollData,
                question: {
                  ...pollData.question,
                  options: shuffleArray(pollData.question.options)
                }
              };
            }
          }
          
          setPoll(pollData);
        }
      } catch (err) {
        setError("Erreur lors du chargement du sondage");
      } finally {
        setLoading(false);
      }
    };

    loadPoll();
  }, []);

  // Voir l'état actuel des votes avant de voter
  const handleShowResultsPreview = async () => {
    if (!poll || resultsPreviewLoading) return;
    setResultsPreviewLoading(true);
    try {
      const pollResults = await SimplePollService.getPollResults(poll.documentId || poll.id);
      setResults(pollResults);
      setShowResultsPreview(true);
    } catch (err) {
      setError("Impossible de charger les résultats");
    } finally {
      setResultsPreviewLoading(false);
    }
  };

  // Revenir au formulaire de vote après avoir vu l'aperçu
  const handleBackToVote = () => {
    setShowResultsPreview(false);
  };

  // Gérer la sélection d'une option
  const handleOptionSelect = (index) => {
    if (!hasVoted && !voteSubmitted) {
      setSelectedOption(index);
    }
  };

  // Soumettre le vote
  const handleSubmit = async () => {
    if (selectedOption === null || !poll || submitting) return;

    try {
      setSubmitting(true);
      setError(null);

      // Récupérer l'option sélectionnée depuis le tableau mélangé
      const selectedOptionData = poll.question.options[selectedOption];
      const optionText = selectedOptionData.text;
      const selectedClubId = selectedOptionData.id;

      // Trouver l'index original dans l'ordre non mélangé pour la soumission du vote
      let originalIndex = selectedOption;
      if (originalOptionsOrder && selectedClubId) {
        const originalIndexFound = originalOptionsOrder.findIndex(opt => opt.id === selectedClubId);
        if (originalIndexFound !== -1) {
          originalIndex = originalIndexFound;
        }
      }

      // Utiliser documentId (stable) au lieu de id (peut changer lors des modifications)
      const pollDocumentId = poll.documentId || poll.id;
      const response = await SimplePollService.submitVote(pollDocumentId, originalIndex, optionText, turnstileToken);

      if (response.success) {
        setVoteSubmitted(true);
        setHasVoted(true);
        setUserChoice(optionText);

        // Charger les résultats
        const pollResults = await SimplePollService.getPollResults(poll.documentId || poll.id);
        setResults(pollResults);
        
        // Si le vote était déjà effectué, recharger l'état complet
        if (response.alreadyVoted) {
          // Recharger le sondage pour synchroniser l'état
          const pollResponse = await SimplePollService.getCurrentPoll();
          if (pollResponse.success && pollResponse.poll) {
            setPoll(pollResponse.poll);
            const pollDocumentIdReloaded = pollResponse.poll.documentId || pollResponse.poll.id;
            const userChoiceReloaded = await SimplePollService.getUserChoice(pollDocumentIdReloaded);
            if (userChoiceReloaded) {
              setUserChoice(userChoiceReloaded);
            }
          }
        }
      } else {
        setError(response.error || "Erreur lors de la soumission du vote");
        turnstileRef.current?.reset();
        setTurnstileToken(null);
      }
    } catch (err) {
      setError("Erreur lors de la soumission du vote");
      turnstileRef.current?.reset();
      setTurnstileToken(null);
    } finally {
      setSubmitting(false);
    }
  };

  // Fonction pour afficher les résultats
  const renderResults = () => {
    if (!results || !results.results) return null;

    return (
      <ResultsContainer>
        <ResultsTitle>
          <FaChartBar />
          Résultats du sondage
        </ResultsTitle>
        {results.results.map((result, index) => (
          <ResultItem key={index} $isUserChoice={result.text === userChoice}>
            <ResultHeader>
              <ResultText>
                {result.text}
                {result.text === userChoice && <FaCheckCircle />}
              </ResultText>
              <ResultVotes>
                {result.votes} votes ({result.percentage}%)
              </ResultVotes>
            </ResultHeader>
            <ResultBar>
              <ResultProgress 
                $percentage={result.percentage} 
                $isUserChoice={result.text === userChoice}
              />
            </ResultBar>
          </ResultItem>
        ))}
        <TotalVotes>
          Total : {results.totalVotes} votes
        </TotalVotes>
        {timeUntilNextVote > 0 && (
          <div style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--gray)' }}>
            Prochain vote possible dans {formatTimeUntilNextVote(timeUntilNextVote)}
          </div>
        )}
      </ResultsContainer>
    );
  };

  // Fonction utilitaire pour générer l'URL du club
  const getClubUrl = (clubName) => {
    if (!clubName) return '#';
    return createClubUrl(clubName);
  };

  // Affichage du loading
  if (loading) {
    return (
      <PollContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <LoadingSpinner />
        </div>
      </PollContainer>
    );
  }

  // Affichage d'erreur
  if (!poll) {
    return (
      <PollContainer>
        <ErrorMessage>
          Aucun sondage en cours.
        </ErrorMessage>
      </PollContainer>
    );
  }

  // Logs de debug supprimés

  return (
    <PollContainer>
      <PollHeader>
        {poll && poll.description && (
          <PollDescription>{poll.description}</PollDescription>
        )}
      </PollHeader>

      {poll && <PollQuestion>{poll.question.text}</PollQuestion>}

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {isUpcoming ? (
        // Sondage actif mais votes pas encore ouverts : afficher les clubs à venir + date d'ouverture
        <>
          <OptionsContainer>
            {poll && poll.question.options.map((option, index) => {
              const clubLogo = option.logo;
              const clubUrl = getClubUrl(option.text);
              return (
                <OptionButton
                  key={option.id}
                  $selected={false}
                  disabled
                  style={{
                    display: 'flex', alignItems: 'center',
                    color: hoveredOption === index ? '#4a6bce' : undefined,
                    transition: 'all 0.2s',
                    cursor: 'default',
                  }}
                  onMouseEnter={() => setHoveredOption(index)}
                  onMouseLeave={() => setHoveredOption(null)}
                >
                  <ClubLogoRound
                    logo={clubLogo}
                    alt={option.text}
                    hovered={hoveredOption === index}
                    onClick={e => { e.stopPropagation(); window.open(clubUrl, '_blank'); }}
                  />
                  <span
                    style={{
                      cursor: 'pointer',
                      color: hoveredOption === index ? '#4a6bce' : '#222',
                      fontWeight: 500,
                      transition: 'all 0.2s',
                      textDecoration: hoveredOption === index ? 'underline' : 'none',
                    }}
                    onClick={() => window.open(clubUrl, '_blank')}
                  >
                    {option.text}
                  </span>
                  <OptionIcon $selected={false}>
                    <BsCircle />
                  </OptionIcon>
                </OptionButton>
              );
            })}
          </OptionsContainer>
          <UpcomingDateMessage>
            Les votes ouvrent le {votesStartDate ? new Date(votesStartDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
          </UpcomingDateMessage>
          {previousWinner && (
            <PreviousWinnerBlock>
              <div style={{ marginBottom: '0.25rem' }}>
                <strong>Sondage précédent :</strong> {previousWinner.pollTitle}
              </div>
              <div>
                <FaCrown />
                <strong>Gagnant :</strong> {previousWinner.winnerText}
                {previousWinner.totalVotes != null && (
                  <span> — {previousWinner.winnerVotes} vote{previousWinner.winnerVotes > 1 ? 's' : ''} ({previousWinner.totalVotes} total)</span>
                )}
              </div>
            </PreviousWinnerBlock>
          )}
        </>
      ) : (voteSubmitted || hasVoted || showResultsPreview) ? (
        // Affichage des résultats (ou aperçu avant vote)
        <ResultsContainer>
          <ResultsTitle>
            <FaChartBar />
            {showResultsPreview && !hasVoted ? 'Résultats actuels (aperçu)' : 'Résultats du sondage'}
          </ResultsTitle>
      {results && results.results && (() => {
            // Calculer l'index du gagnant (max votes)
            const winnerIndex = results.results.reduce((bestIdx, r, idx, arr) => (arr[bestIdx] && arr[bestIdx].votes > r.votes ? bestIdx : idx), 0);
            return results.results.map((result, index) => {
            // Recherche du club pour le logo
            const club = poll.question.options.find(opt => opt.text === result.text);
            const clubLogo = club && club.logo;
            const clubUrl = getClubUrl(result.text);
            return (
              <ResultItem
                key={index}
                $isUserChoice={result.text === userChoice}
                onMouseEnter={() => setHoveredResult(index)}
                onMouseLeave={() => setHoveredResult(null)}
              >
                <ResultHeader>
                  <span
                    style={{
                      display: 'flex', alignItems: 'center', cursor: 'pointer',
                      textDecoration: hoveredResult === index ? 'underline' : 'none',
                      color: hoveredResult === index ? '#4a6bce' : undefined,
                      transition: 'all 0.2s',
                    }}
                    onClick={() => window.open(clubUrl, '_blank')}
                  >
                    <ClubLogoRound
                      logo={clubLogo}
                      alt={result.text}
                      hovered={hoveredResult === index}
                      onClick={e => { e.stopPropagation(); window.open(clubUrl, '_blank'); }}
                    />
                    {result.text}
                    {index === winnerIndex && <FaCrown style={{ marginLeft: 6, color: '#d4af37' }} />}
                    {result.text === userChoice && <FaCheckCircle style={{ marginLeft: 6, color: '#4a6bce' }} />}
                  </span>
                  <ResultVotes>
                    {result.votes} votes ({result.percentage}%)
                  </ResultVotes>
                </ResultHeader>
                <ResultBar>
                  <ResultProgress 
                    $percentage={result.percentage} 
                    $isUserChoice={result.text === userChoice}
                  />
                </ResultBar>
              </ResultItem>
            );
          });})()}
          <TotalVotes>
            Total : {results && results.totalVotes} votes
          </TotalVotes>
          {pollFinished && (
            <div style={{ textAlign: 'center', marginTop: '0.75rem', color: 'var(--gray)', fontStyle: 'italic' }}>
              Fin du sondage
            </div>
          )}
          {timeUntilNextVote > 0 && !showResultsPreview && (
            <div style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--gray)' }}>
              Prochain vote possible dans {formatTimeUntilNextVote(timeUntilNextVote)}
            </div>
          )}
          {showResultsPreview && !hasVoted && (
            <SecondaryButton onClick={handleBackToVote}>
              <FaVoteYea />
              Revenir au vote
            </SecondaryButton>
          )}
        </ResultsContainer>
      ) : (
        // Affichage des options de vote
        <>
          <OptionsContainer>
            {poll && poll.question.options.map((option, index) => {
              const clubLogo = option.logo;
              const clubUrl = getClubUrl(option.text);
              return (
                <OptionButton
                  key={option.id}
                  $selected={selectedOption === index}
                  disabled={hasVoted}
                  onClick={() => handleOptionSelect(index)}
                  onMouseEnter={() => setHoveredOption(index)}
                  onMouseLeave={() => setHoveredOption(null)}
                  style={{
                    display: 'flex', alignItems: 'center',
                    // textDecoration supprimé pour éviter tout soulignement
                    color: hoveredOption === index ? '#4a6bce' : undefined,
                    transition: 'all 0.2s',
                  }}
                >
                  <ClubLogoRound
                    logo={clubLogo}
                    alt={option.text}
                    hovered={hoveredOption === index}
                    onClick={e => { e.stopPropagation(); window.open(clubUrl, '_blank'); }}
                  />
                  <span
                    style={{
                      cursor: 'default',
                      color: selectedOption === index ? '#132e57' : '#222',
                      fontWeight: selectedOption === index ? 700 : 500,
                      transition: 'all 0.2s'
                      // PAS de textDecoration ici !
                    }}
                  >
                    {option.text}
                  </span>
                  <OptionIcon $selected={selectedOption === index}>
                    {selectedOption === index ? <BsCheckCircleFill /> : <BsCircle />}
                  </OptionIcon>
                </OptionButton>
              );
            })}
          </OptionsContainer>

          <Turnstile
            ref={turnstileRef}
            siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
            onSuccess={setTurnstileToken}
            onExpire={() => setTurnstileToken(null)}
            onError={() => setTurnstileToken(null)}
            options={{ theme: 'auto' }}
          />

          <SubmitButton
            onClick={handleSubmit}
            disabled={selectedOption === null || submitting || hasVoted || !turnstileToken}
            className={selectedOption !== null && !submitting && !hasVoted && turnstileToken ? 'shine' : ''}
          >
            {submitting ? (
              <>
                <LoadingSpinner />
                Envoi en cours...
              </>
            ) : hasVoted ? (
              <>
                <FaCheckCircle />
                Déjà voté
              </>
            ) : (
              <>
                <FaVoteYea />
                Voter
              </>
            )}
          </SubmitButton>

          <SecondaryButton
            onClick={handleShowResultsPreview}
            disabled={resultsPreviewLoading || hasVoted}
          >
            {resultsPreviewLoading ? (
              <>
                <LoadingSpinner />
                Chargement...
              </>
            ) : (
              <>
                <FaChartBar />
                Voir les résultats actuels
              </>
            )}
          </SecondaryButton>
        </>
      )}
    </PollContainer>
  );
};

export default SimplePoll; 